/**
 * Moment filtering + formatting helpers, ported from the inline script of
 * app/templates/game_match_summary/index.html:
 *   parseGameCode (~1965), getFieldPhase (~2568), getVerticalZone (~2595),
 *   getPhaseFromFrame (~2605), getCurrentFilteredMoments (~2664),
 *   normalizeMomentType (~3078), formatMomentType (~3826),
 *   getFieldZoneName (~3848), detectHalftime (~4113).
 * The zone and channel lines, and the direction step (attackSign), are not
 * the script's.
 */
import {
  DEFAULT_LENGTH_M, DEFAULT_WIDTH_M, PAGE_CHANNELS, PAGE_ZONES, ZONE_NAMES, type ZoneCode,
  channelCode, zoneCode,
} from '../../lib/pitchZones'
import { hasNoScore } from './momentScores'
import type { DangerosityPoint, MatchPeriod, Moment, TeamMoments } from './types'

// Canonical game_code parsing (mirror of app/utils/game_code.py). game_code is
// AWAY@HOME[@YYYYMMDD]; this returns clean team codes with the hidden date
// token (or a legacy glued suffix) stripped.
export function parseGameCode(code: string): { away: string; home: string } {
  if (!code || code.indexOf('@') === -1) return { away: code, home: code }
  const parts = code.split('@')
  // `indexOf('@') !== -1` above guarantees a first part; naming it once is
  // what lets both branches below use it without an assertion.
  const first = parts[0] ?? code
  let away = first
  let home = parts[1] ?? first
  if (!(parts.length >= 3 && parts[2])) {
    away = away.replace(/\d{4,}\w*$/, '') || away // strip legacy glued date
    home = home.replace(/\d{4,}\w*$/, '') || home
  }
  return { away, home }
}

// The sign that turns a raw position into the attacking view of team in
// phase, or null with no direction. Both x and y take it: the attacking
// view is the raw pitch turned 180 degrees, never mirrored.
function attackSign(homeAttackDirH1: string | null, team: number, phase: number): number | null {
  if (homeAttackDirH1 !== 'left' && homeAttackDirH1 !== 'right') return null
  // Positive toward the goal home attacks in the first half; the away
  // team (semantic team 0) attacks the other way; teams switch sides at
  // halftime.
  return (homeAttackDirH1 === 'left' ? -1 : 1) * (team === 0 ? -1 : 1) * (phase === 2 ? -1 : 1)
}

// The zone code (lib/pitchZones) of x in cm, or null with no direction.
// homeAttackDirH1 is the served home_team_start_direction: the direction
// home attacks in the first half, 'left' or 'right'.
// team is SEMANTIC: 1 = home, 0 = away. Which TRACKING index is home is a
// per-feed fact (game_facts.tracking_home_index) — callers holding a
// tracking team_id translate through it before calling (UT@Clemson has
// home on index 0; passing its tracking ids raw mirrors every zone).
// phase: 1 = first half, 2 = second half (defaults to first half)
// lengthM: the length the game's zones are cut on, metres.
export function fieldZoneCode(
  homeAttackDirH1: string | null,
  x: number,
  team = 1,
  phase = 1,
  lengthM: number = DEFAULT_LENGTH_M,
): ZoneCode | null {
  const sign = attackSign(homeAttackDirH1, team, phase)
  return sign === null ? null : zoneCode((sign * x) / 100, lengthM)
}

// The page's zone filter value of x, or null with no direction.
export function getFieldPhase(
  homeAttackDirH1: string | null,
  x: number,
  team = 1,
  phase = 1,
  lengthM: number = DEFAULT_LENGTH_M,
): string | null {
  const code = fieldZoneCode(homeAttackDirH1, x, team, phase, lengthM)
  return code === null ? null : PAGE_ZONES[code]
}

// The semantic team (1 = home, 0 = away) of a moment's TRACKING team_id,
// through the per-feed home index. A moment with no team_id reads as home.
export function semanticTeam(teamId: number | null | undefined, trackingHomeIndex: 0 | 1 = 1): number {
  if (teamId == null) return 1
  return teamId === trackingHomeIndex ? 1 : 0
}

// A moment's x in metres for its zone: a pass or run's end — a staff mark's
// too (its end in cm, else its start: SDET review of #946, a staff pass was
// zoned by where it started while every other pass is zoned by where it
// ends) — any other staff mark's x (cm), a possession window's median
// ball_x (cm), else x or ball_x. Null when the field is absent, so the
// moment has no zone.
export function momentX(moment: Moment): number | null {
  const type = moment.type || moment.event_type || ''
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const passOrRun = type.includes('pass') || type.includes('run')
  if (String(moment.id ?? '').startsWith('annotation_')) {
    const xCm = (passOrRun ? num(moment.target_x) : null) ?? num(moment.x)
    return xCm === null ? null : xCm / 100
  }
  if (passOrRun) {
    return num(moment.target_x) ?? num(moment.player_x)
  }
  if (type === 'possession' || type === 'out-possession') {
    const ballXCm = num(moment.ball_x)
    return ballXCm === null ? null : ballXCm / 100
  }
  return num(moment.x) ?? num(moment.ball_x)
}

// A moment's y in metres for its channel, at the point momentX reads x,
// kind by kind: a staff pass or run's target_y, else its y, and any other
// staff mark's y (cm); a tracked pass or run's target_y, else its
// player_y; a possession window's ball_y (cm) only; a press's ball_y, else
// its y; any other moment's y, else its ball_y.
export function momentY(moment: Moment): number | null {
  const type = moment.type || moment.event_type || ''
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const passOrRun = type.includes('pass') || type.includes('run')
  if (String(moment.id ?? '').startsWith('annotation_')) {
    const yCm = (passOrRun ? num(moment.target_y) : null) ?? num(moment.y)
    return yCm === null ? null : yCm / 100
  }
  if (passOrRun) {
    return num(moment.target_y) ?? num(moment.player_y)
  }
  if (type === 'possession' || type === 'out-possession') {
    const ballYCm = num(moment.ball_y)
    return ballYCm === null ? null : ballYCm / 100
  }
  if (type.includes('press')) {
    return num(moment.ball_y) ?? num(moment.y)
  }
  return num(moment.y) ?? num(moment.ball_y)
}

// Corners and goals carry the SEMANTIC side as team_id: corners are
// attributed from zone and direction (corner_moments.py), goals come from
// staff annotations and Match Lab labels. Every other moment kind carries
// a TRACKING index.
export function hasSemanticTeam(moment: Moment): boolean {
  const type = moment.type || moment.event_type || ''
  return type === 'corner' || type === 'goals' || type.endsWith('_goal')
}

// A moment's semantic team: as carried for corners and goals, else its
// tracking team_id read through the per-feed home index.
export function momentTeam(moment: Moment, trackingHomeIndex: 0 | 1 = 1): number {
  if (hasSemanticTeam(moment) && moment.team_id != null) return moment.team_id
  return semanticTeam(moment.team_id, trackingHomeIndex)
}

// The page's channel filter value of y in cm, or null with no direction.
// The parameters are fieldZoneCode's; y is turned by the same sign as x,
// then cut by lib/pitchZones channelCode.
export function getVerticalZone(
  homeAttackDirH1: string | null,
  y: number,
  team = 1,
  phase = 1,
): string | null {
  const sign = attackSign(homeAttackDirH1, team, phase)
  const code = sign === null ? null : channelCode((sign * y) / 100, DEFAULT_WIDTH_M)
  return code === null ? null : PAGE_CHANNELS[code]
}

export interface Halftime {
  startFrame: number
  endFrame: number
  gapFrames: number
}

// Halftime: between the first two periods when the game's periods are
// known (the dangerosity response's metadata), else the dangerosity
// series' large frame gap. A game whose second half starts on the next
// frame has no gap (game 249): only its periods can say where half time is.
/** The press types among the out-of-possession rail's picks: everything
 *  but its possession-windows chip ('possession') and its goals chip
 *  ('goals' — the same both-teams list as the in-possession rail's). */
export function pressTypesOf(picks: string[]): string[] {
  return picks.filter((t) => t !== 'possession' && t !== 'goals')
}

export function detectHalftime(
  dangerosityData: DangerosityPoint[] | null,
  periods?: MatchPeriod[] | null,
): Halftime | null {
  const halves = (periods ?? []).filter((p) => p.period === 1 || p.period === 2)
  const first = halves.find((p) => p.period === 1)
  const second = halves.find((p) => p.period === 2)
  if (first && second) {
    return {
      startFrame: first.end_frame,
      endFrame: second.start_frame,
      gapFrames: second.start_frame - first.end_frame,
    }
  }
  if (!dangerosityData || dangerosityData.length < 2) return null

  const MIN_GAP_FRAMES = 10000 // 5.5+ minutes indicates halftime
  const MIN_FIRST_HALF_FRAMES = 60000 // At least 33 minutes before halftime

  for (let i = 0; i + 1 < dangerosityData.length; i++) {
    const cur = dangerosityData[i]
    const next = dangerosityData[i + 1]
    if (cur === undefined || next === undefined) continue
    const currentFrame = cur.frame
    const nextFrame = next.frame
    const gap = nextFrame - currentFrame

    // Check if this is a large gap after sufficient play time
    if (gap > MIN_GAP_FRAMES && currentFrame > MIN_FIRST_HALF_FRAMES) {
      return { startFrame: currentFrame, endFrame: nextFrame, gapFrames: gap }
    }
  }

  return null
}

// Get phase (half) from frame number using halftime detection.
// `frame` may be undefined (the template passed moment.frame straight
// through); `undefined < endFrame` is false in JS, so that reads as phase 2 —
// preserved here explicitly.
export function getPhaseFromFrame(halftime: Halftime | null, frame: number | undefined): number {
  if (!halftime) return 1
  return frame !== undefined && frame < halftime.endFrame ? 1 : 2
}

// The half a moment's zone is oriented by: its phase when it has one;
// else, once half time is known, the half its frame falls in (start_frame
// for a possession window, which has no frame); else null, and the moment
// has no zone. A phase other than 1 or 2 is null too: no direction is
// known for it.
export function momentHalf(moment: Moment, halftime: Halftime | null): 1 | 2 | null {
  if (moment.phase === 1 || moment.phase === 2) return moment.phase
  if (moment.phase != null) return null
  const frame = typeof moment.frame === 'number' ? moment.frame : moment.start_frame
  if (!halftime || typeof frame !== 'number') return null
  return frame < halftime.endFrame ? 1 : 2
}

/** Inputs getCurrentFilteredMoments read off the template's global `state`. */
/**
 * Filter moments by their tactical score.
 *
 * THE RANGE IS NOT 0..1. Measured against LAG@SKC's 4,039 moments, scores run
 * from -0.809 to 0.878: runs in particular go negative. A control built as a
 * 0-100% slider would have silently excluded every negative-scored run and
 * looked like it was working, so the bounds come from the loaded moments
 * (scoreBounds) rather than from an assumption.
 */
export type ScoreFilter =
  | { mode: 'off' }
  | { mode: 'above'; value: number }
  | { mode: 'below'; value: number }
  | { mode: 'between'; min: number; max: number }

/** The lowest and highest score present, for sizing the control, and
 *  whether any moment is scored (null when there is no moment): the
 *  scored moments only, so a moment with no score (hasNoScore) sets
 *  neither bound. */
export function scoreBounds(moments: Moment[]): { min: number; max: number; scored: boolean | null } {
  let min = Infinity
  let max = -Infinity
  for (const m of moments) {
    if (typeof m.score !== 'number' || Number.isNaN(m.score)) continue
    if (m.score < min) min = m.score
    if (m.score > max) max = m.score
  }
  // No scored moments: a symmetric default, because the data is signed.
  if (min === Infinity) return { min: -1, max: 1, scored: moments.length > 0 ? false : null }
  return { min, max, scored: true }
}

/**
 * Whether one moment passes the score filter.
 *
 * A moment whose score is absent or not a number fails every active filter —
 * including one served with a null score (hasNoScore). "Above 0.5" is a
 * claim about a score, and a moment that has none does not meet it. The
 * "no score" cards used to ride through every filter because they are
 * labelled as such, but a view filtered to Score ≥ 0.67 full of "no score"
 * cards reads as the filter not working (Ben, 2026-10-26). With the filter
 * off, everything passes and the score is never consulted.
 */
export function passesScoreFilter(moment: Moment, filter: ScoreFilter | undefined): boolean {
  if (!filter || filter.mode === 'off') return true
  if (hasNoScore(moment)) return false
  const score = moment.score
  if (typeof score !== 'number' || Number.isNaN(score)) return false
  if (filter.mode === 'above') return score >= filter.value
  if (filter.mode === 'below') return score <= filter.value
  // Inclusive at both ends, and tolerant of the bounds arriving reversed —
  // a control that lets you drag the handles past each other should narrow
  // the range, not empty it.
  const low = Math.min(filter.min, filter.max)
  const high = Math.max(filter.min, filter.max)
  return score >= low && score <= high
}

export interface FilterState {
  viewingAsTeam: number
  /** Which TRACKING index is the home side (game_facts fact). Optional,
   *  default 1 — the pre-column convention — so existing callers and
   *  tests keep their exact behaviour. */
  trackingHomeIndex?: 0 | 1
  selectedTeam: number
  activeEventFilters: Set<string>
  activeFieldPhases: Set<string>
  activeVerticalZones: Set<string>
  homeMoments: TeamMoments | null
  awayMoments: TeamMoments | null
  possessionMoments: { in_possession?: Moment[]; out_of_possession?: Moment[] } | null
  manualMoments: Moment[]
  /** The served home_team_start_direction: the direction home attacks in
   *  the first half. Null while unset or loading, and then no moment has a
   *  zone. */
  homeTeamStartDirection: string | null
  /** The game's zone length, metres; 105 m when absent. */
  zoneLengthM?: number
  halftime: Halftime | null
  /** Optional so every existing caller and test keeps its exact behaviour. */
  scoreFilter?: ScoreFilter
  /**
   * Player filter (2026-09-03): keep only moments involving any selected
   * player — as the actor (`player`), the passer (`from_player`) or the
   * receiver (`to_player`). Possession windows carry no player and are
   * therefore excluded while this filter is active: "show me this player"
   * is a claim about involvement, and a team-level window makes none.
   */
  playersSelected?: Set<string>
}

/** The distinct player identities present in a list of moments, sorted.
    Jersey numbers sort numerically, names alphabetically after them. */
export function playersInMoments(moments: Moment[]): string[] {
  const seen = new Set<string>()
  for (const m of moments) {
    for (const p of [m.player, m.from_player, m.to_player]) {
      if (p !== null && p !== undefined && `${p}`.trim() !== '') seen.add(`${p}`)
    }
  }
  return [...seen].sort((a, b) => {
    const na = Number(a)
    const nb = Number(b)
    const aNum = !Number.isNaN(na)
    const bNum = !Number.isNaN(nb)
    if (aNum && bNum) return na - nb
    if (aNum) return -1
    if (bNum) return 1
    return a.localeCompare(b)
  })
}

/** Whether a moment involves any of the selected players. */
export function involvesPlayer(m: Moment, players: Set<string>): boolean {
  for (const p of [m.player, m.from_player, m.to_player]) {
    if (p !== null && p !== undefined && players.has(`${p}`)) return true
  }
  return false
}

// Get currently filtered moments based on active filters (template ~2664).
// NOTE: the 'possession_segments' branch of the template read
// state.possessionSegments, which nothing on the page ever populated (no
// filter emits that value either) — dead branch, not ported.
export function getCurrentFilteredMoments(s: FilterState): Moment[] {
  let moments: Moment[] = []

  // Step 1: Determine which team's moments to show based on viewingAsTeam
  // (a tracking index, mapped through the per-feed home index);
  // selectedTeam controls possession state
  const homeIdx = s.trackingHomeIndex ?? 1
  const teamMoments = s.viewingAsTeam === homeIdx ? s.homeMoments : s.awayMoments

  // Step 2: If event filters are active, get those specific events
  if (s.activeEventFilters.size > 0) {
    s.activeEventFilters.forEach((filter) => {
      // Handle progressive_passes and supportive_passes by filtering the passes array
      if (filter === 'progressive_passes' || filter === 'supportive_passes') {
        const passType = filter === 'progressive_passes' ? 'progressive_pass' : 'supportive_pass'
        if (teamMoments && teamMoments.passes) {
          moments = moments.concat(teamMoments.passes.filter((m) => m.type === passType))
        }
      }
      // Handle progressive_runs and supportive_runs by filtering the runs array
      else if (filter === 'progressive_runs' || filter === 'supportive_runs') {
        const runType = filter === 'progressive_runs' ? 'progressive_run' : 'supportive_run'
        if (teamMoments && teamMoments.runs) {
          moments = moments.concat(teamMoments.runs.filter((m) => m.type === runType))
        }
      }
      // The possession windows of the phase on view, picked like any other
      // type (they were reachable only by clearing every type): in
      // possession, the viewing team's; out of possession, the opponent's.
      else if (filter === 'possession') {
        const out = s.selectedTeam !== 0
        const windows = out ? s.possessionMoments?.out_of_possession : s.possessionMoments?.in_possession
        moments = moments.concat((windows || []).map((m) => ({
          ...m,
          type: out ? 'out-possession' : 'possession',
        })))
      } else if (teamMoments && teamMoments[filter]) {
        moments = moments.concat(teamMoments[filter] as Moment[])
      }
    })
  }
  // No moment type chosen: no moments. Possession windows used to fill the
  // list here, and a feed with every filter cleared looked like a feed of
  // results (Ben, 2026-10-26) — "Possession" is a type to pick like any other.

  // Step 3: Apply field phase filters if active
  if (s.activeFieldPhases.size > 0) {
    moments = moments.filter((moment) => {
      // The moment's x (momentX), team and half, as the moment card reads
      // them. getFieldPhase wants cm and the semantic side. A moment with
      // no team_id has no zone, as it has no channel in step 4: read as
      // home, an away moment landed in the opposite third.
      const xPosition = momentX(moment)
      const team = moment.team_id == null ? null : momentTeam(moment, homeIdx)
      const momentPhase = momentHalf(moment, s.halftime)

      // A moment with no zone matches no zone filter.
      const fieldPhase = momentPhase === null || xPosition === null || team === null ? null
        : getFieldPhase(s.homeTeamStartDirection, xPosition * 100, team, momentPhase,
          s.zoneLengthM)
      return fieldPhase !== null && s.activeFieldPhases.has(fieldPhase)
    })
  }

  // Step 4: Apply channel filters if active
  if (s.activeVerticalZones.size > 0) {
    moments = moments.filter((moment) => {
      // The moment's y (momentY), team and half, as step 3 reads x; a
      // moment with no team_id has no channel. getVerticalZone wants cm
      // and the semantic side.
      const yPosition = momentY(moment)
      const team = moment.team_id == null ? null : momentTeam(moment, homeIdx)
      const momentPhase = momentHalf(moment, s.halftime)

      // A moment with no channel matches no channel filter.
      const channel = momentPhase === null || yPosition === null || team === null ? null
        : getVerticalZone(s.homeTeamStartDirection, yPosition * 100, team, momentPhase)
      return channel !== null && s.activeVerticalZones.has(channel)
    })
  }

  // Player filter — before manual moments for the same reason as the score
  // filter below: an analyst's own marks are never filtered away.
  if (s.playersSelected && s.playersSelected.size > 0) {
    const players = s.playersSelected
    moments = moments.filter((m) => involvesPlayer(m, players))
  }

  // Score filter, applied BEFORE manual moments are added so that an
  // analyst's own marks keep the "always shown regardless of filters"
  // invariant the zone and event filters already respect. A manual moment
  // usually carries no score at all, so scoring it out would make the one
  // category a human placed by hand the easiest to lose.
  if (s.scoreFilter && s.scoreFilter.mode !== 'off') {
    moments = moments.filter((m) => passesScoreFilter(m, s.scoreFilter))
  }

  // Always include manual moments regardless of filters
  if (s.manualMoments && s.manualMoments.length > 0) {
    moments = moments.concat(s.manualMoments)
  }

  // Sort by frame number
  moments.sort((a, b) => (a.frame || a.start_frame || 0) - (b.frame || b.start_frame || 0))

  return moments
}

// Normalize moment type to base type accepted by rating API
// API accepts: 'pass', 'run', 'regain', 'possession_segment'
export function normalizeMomentType(momentType: string): string | null {
  // Handle old possession types - don't rate these as they're not in the valid list
  if (momentType === 'possession' || momentType === 'out-possession') {
    return null // Skip rating for old possession moments
  }

  // Handle possession_segment from new possession model
  if (momentType === 'possession_segment') {
    return 'possession_segment'
  }

  // Normalize pass subtypes
  if (momentType.includes('pass')) {
    return 'pass'
  }

  // Normalize run subtypes
  if (momentType.includes('run')) {
    return 'run'
  }

  // Return as-is if it's already a base type
  if (['pass', 'run', 'regain', 'possession_segment', 'pressing'].includes(momentType)) {
    return momentType
  }

  // Unknown type
  console.warn('Unknown moment type:', momentType)
  return null
}

// Format moment type for display (Title Case)
export function formatMomentType(type: string | undefined, currentPressTypes: string[]): string {
  if (!type) return 'Moment'

  // Helper: convert to Title Case
  const toTitleCase = (str: string) =>
    str
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')

  // A goal is a goal: the stored types carry provenance ('shot_goal',
  // 'corner_goal' — kind_outcome from the annotations API) and the cards
  // read them out loud as "Shot Goal" (Ben, 2026-10-15). The provenance
  // stays in the data; the card just says what happened.
  if (type === 'goals' || type.endsWith('_goal')) return 'Goal'

  // For pressing moments, use the selected filter label
  if (type === 'pressing' && currentPressTypes?.length === 1) {
    const only = currentPressTypes[0]
    if (only !== undefined) return toTitleCase(only)
  }

  return toTitleCase(type)
}

// Get field zone name from x coordinate (display labels; template ~3848),
// or null with no direction. The parameters are fieldZoneCode's.
export function getFieldZoneName(
  homeAttackDirH1: string | null,
  x: number,
  team = 1,
  phase = 1,
  lengthM: number = DEFAULT_LENGTH_M,
): string | null {
  const code = fieldZoneCode(homeAttackDirH1, x, team, phase, lengthM)
  return code === null ? null : ZONE_NAMES[code]
}
